import { useEffect, useState, type ReactNode } from "react";
import PlatformProbe from "./PlatformProbe";
import {
  installProbeChord,
  probeRequestedByQuery,
  uninstallProbeChord,
} from "../lib/probeChord";

/**
 * The platform probe as an OVERLAY over the running app.
 *
 * WHY AN OVERLAY, AND NOT A REPLACEMENT. Sigge found this on the bench:
 * "if it's in probe it doesn't take the reboot command". Exactly right, and it
 * is what made the fleet console read SENT forever with no artifact. `App`
 * returns `<PlatformProbe />` as an early return, ahead of the component that
 * mounts the heartbeat and the realtime command subscription - so a panel
 * showing the probe has quietly stopped listening to the fleet. A diagnostic
 * that takes the device off the channel you are trying to diagnose it over is
 * worse than no diagnostic: it manufactures the symptom.
 *
 * So the chord route renders the probe ON TOP of the app, which keeps running
 * underneath: still heartbeating, still draining commands, still playing
 * signage. You can read the panel's inventory and reboot it from the CMS in the
 * same minute.
 *
 * `?probe=1` deliberately stays a hard early return in `App`. That path is
 * first light - it must render on a panel where the launcher does not, so it
 * may not depend on the app mounting at all. The two routes answer different
 * questions and the probe says which one you are on, because "is this device
 * online right now?" has a different answer in each.
 *
 * It is a WRAPPER rather than another branch inside `App`'s router because
 * `App` is deliberately hook-free ("no hooks here so the chosen child can use
 * hooks freely"), and the chord needs state. Wrapping also means the probe is
 * reachable from ANY screen - the launcher, a scheme, a black signage surface,
 * the pairing screen. The panels this exists for are the ones where the normal
 * UI is not rendering.
 */
export default function ProbeChordHost({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    installProbeChord(() => setOpen(true));
    return () => uninstallProbeChord();
  }, []);

  // Back closes it. On Samsung that arrives as keyCode 10009 and is translated
  // to "Escape" by installTizenKeyBridge, so the one key a guest would reach
  // for is the one that works - and a panel cannot be left stuck on a
  // diagnostic page with no way back to the launcher.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape" && e.key !== "Backspace") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setOpen(false);
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  return (
    <>
      {children}
      {/* The query route renders its own probe inside App, so never stack a
          second one on top of it. */}
      {open && !probeRequestedByQuery() && (
        <div style={OVERLAY} data-testid="probe-overlay">
          <PlatformProbe live />
        </div>
      )}
    </>
  );
}

/**
 * Fixed rather than absolute so it covers the panel regardless of what the app
 * underneath did to the document flow, and opaque so a signage video behind it
 * cannot show through and make the readings hard to photograph.
 */
const OVERLAY: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2147483000,
  background: "#0b1020",
  overflow: "auto",
};
